import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'

/**
 * #513: мини-окошко с лекцией, чтобы смотреть её во время тренировки.
 *
 * Просьба подопечного через куратора: «можно сделать мини экран (окошко в углу)
 * с лекцией, чтобы можно было смотреть лекции во время тренировки».
 *
 * Ключевое ограничение: перенос <iframe> между родителями в DOM ПЕРЕЗАГРУЖАЕТ
 * его, то есть видео начнётся сначала. Поэтому кадр живёт в единственном месте —
 * портале в body — и никогда не переезжает. Меняется только позиция обёртки:
 *  - страница лекции открыта → обёртка накрывает якорь (плейсхолдер в вёрстке),
 *    и визуально это обычный плеер в потоке;
 *  - клиент ушёл на другой экран → обёртка сжимается в угол экрана.
 *
 * #642: пришвартованный кадр стоит `position: absolute` в координатах документа,
 * а не `fixed` во вьюпорте. Раньше он был fixed и догонял якорь JS-ом по
 * scroll-событию — а на iOS инерционную прокрутку WebView крутит композитор, и
 * scroll-события приходят с запозданием: чёрный якорь уезжал вместе с текстом,
 * а кадр догонял его рывками («видео плавает поверх урока»). Документный
 * absolute браузер прокручивает сам, в том же кадре, что и текст. Замер нужен
 * только на смену вёрстки (ресайз, подгрузка картинок выше видео).
 */

export interface MiniPlayerSource {
  /** Ключ ролика: смена ключа = новый iframe (и новая загрузка). */
  key: string
  url: string
  title: string
  /** Куда возвращать по клику на мини-окне. */
  returnTo: string
}

interface MiniPlayerApi {
  /** Показать ролик и «пришвартовать» его к якорю на странице. */
  attach: (source: MiniPlayerSource, anchor: RefObject<HTMLElement>) => void
  /** Отпустить якорь: страница закрывается, кадр уезжает в угол. */
  detach: (key: string) => void
  /** Убрать плеер совсем. */
  close: () => void
  /** Ключ активного ролика (null — плеера нет). */
  activeKey: string | null
  /**
   * #513 (2-й заход): полноэкранный режим кадра.
   *
   * Раньше «во весь экран» жило на самой странице и работало с её собственным
   * `<iframe>`. Для XL-конспекта такого iframe больше нет — единственный
   * экземпляр плеера теперь здесь, в портале, поэтому и разворачивать его
   * умеет только плеер.
   */
  toggleFullscreen: () => void
  isFullscreen: boolean
}

const MiniPlayerContext = createContext<MiniPlayerApi | null>(null)

/** Размер мини-окна: 16:9, чтобы кадр не искажался. */
const MINI_WIDTH = 176
const MINI_HEIGHT = Math.round((MINI_WIDTH * 9) / 16)
const MINI_MARGIN = 12
/** Отступ снизу — над нижней навигацией, иначе окно накрывает её кнопки. */
const MINI_BOTTOM = 84

type Rect = { top: number; left: number; width: number; height: number }

const sameRect = (a: Rect | null, b: Rect) =>
  !!a && a.top === b.top && a.left === b.left && a.width === b.width && a.height === b.height

export function MiniPlayerProvider({ children }: { children: ReactNode }) {
  const [source, setSource] = useState<MiniPlayerSource | null>(null)
  const [rect, setRect] = useState<Rect | null>(null)
  const anchorRef = useRef<RefObject<HTMLElement> | null>(null)
  const frameRef = useRef<HTMLDivElement>(null)
  const iframeRef = useRef<HTMLIFrameElement>(null)
  // 'css' — fallback, когда Fullscreen API недоступен (Telegram WebView на части
  // устройств его не даёт): кадр растягиваем стилями сами.
  const [fsMode, setFsMode] = useState<'off' | 'native' | 'css'>('off')
  // Ключ активного ролика зеркалим в ref: detach вызывается из cleanup-эффекта
  // страницы и должен решать «мой ли это ролик» без чтения состояния.
  const activeKeyRef = useRef<string | null>(null)
  const navigate = useNavigate()

  const measure = useCallback(() => {
    const el = anchorRef.current?.current
    if (!el) {
      setRect(null)
      return
    }
    const r = el.getBoundingClientRect()
    // Нулевой размер — якорь ещё не в вёрстке (или скрыт): не «схлопываем»
    // кадр, а оставляем прошлую позицию до следующего замера.
    if (r.width < 1 || r.height < 1) return
    // #642: координаты — от начала документа, а не от вьюпорта. Кадр лежит в
    // портале прямо в <body> (margin 0), так что точкой отсчёта берём его:
    // прокрутка документа сдвигает body и якорь одинаково, и разность от неё
    // не зависит — кадр едет вместе со страницей без участия JS.
    const origin = document.body.getBoundingClientRect()
    const next = {
      top: r.top - origin.top,
      left: r.left - origin.left,
      width: r.width,
      height: r.height,
    }
    // Тот же замер — без лишнего ре-рендера на каждый тик скролла и интервала.
    setRect((prev) => (sameRect(prev, next) ? prev : next))
  }, [])

  const attach = useCallback((next: MiniPlayerSource, anchor: RefObject<HTMLElement>) => {
    anchorRef.current = anchor
    activeKeyRef.current = next.key
    setSource((prev) => (prev && prev.key === next.key ? prev : next))
    measure()
  }, [measure])

  const detach = useCallback((key: string) => {
    // Плеер мог уже переключиться на другой ролик — тогда отвязывать нечего.
    if (activeKeyRef.current !== key) return
    anchorRef.current = null
    setRect(null)
  }, [])

  const close = useCallback(() => {
    anchorRef.current = null
    activeKeyRef.current = null
    setRect(null)
    setSource(null)
  }, [])

  // Пришвартованный кадр следует за якорем при смене вёрстки. Прокрутку документа
  // он отрабатывает сам (absolute, см. #642); слушатель scroll остаётся страховкой
  // на случай, если якорь окажется во внутреннем скролл-контейнере.
  useEffect(() => {
    if (!source || !anchorRef.current) return
    let raf = 0
    const onMove = () => {
      cancelAnimationFrame(raf)
      raf = requestAnimationFrame(measure)
    }
    measure()
    window.addEventListener('scroll', onMove, true)
    window.addEventListener('resize', onMove)
    const id = window.setInterval(measure, 500) // страховка на анимации и подгрузку картинок
    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener('scroll', onMove, true)
      window.removeEventListener('resize', onMove)
      window.clearInterval(id)
    }
  }, [source, rect === null, measure])

  // Каскад повторяет тот, что был на странице лекции: сперва сам iframe, затем
  // обёртка, затем CSS. Полноэкранный режим у чужого плеера в WebView срабатывает
  // не всегда, и молча ничего не делать — худший исход.
  const toggleFullscreen = useCallback(async () => {
    if (fsMode !== 'off') {
      if (fsMode === 'native') {
        try { await document.exitFullscreen?.() } catch { /* уже вышли */ }
      }
      try { screen.orientation?.unlock() } catch { /* нет API ориентации */ }
      setFsMode('off')
      return
    }

    try {
      await iframeRef.current?.requestFullscreen()
      try { await (screen.orientation as any).lock('landscape') } catch { /* нет API */ }
      setFsMode('native')
      return
    } catch { /* пробуем обёртку */ }

    try {
      await frameRef.current?.requestFullscreen()
      try { await (screen.orientation as any).lock('landscape') } catch { /* нет API */ }
      setFsMode('native')
      return
    } catch { /* остаётся CSS */ }

    try { await (screen.orientation as any).lock('landscape') } catch { /* нет API */ }
    setFsMode('css')
  }, [fsMode])

  // Выход по Escape/системному жесту — состояние надо вернуть, иначе кнопка
  // останется в положении «Выйти», а кадр уже обычный.
  useEffect(() => {
    const handler = () => {
      if (document.fullscreenElement) return
      setFsMode((m) => (m === 'native' ? 'off' : m))
    }
    document.addEventListener('fullscreenchange', handler)
    return () => document.removeEventListener('fullscreenchange', handler)
  }, [])

  const api = useMemo<MiniPlayerApi>(
    () => ({
      attach,
      detach,
      close,
      activeKey: source?.key ?? null,
      toggleFullscreen,
      isFullscreen: fsMode !== 'off',
    }),
    [attach, detach, close, source?.key, toggleFullscreen, fsMode],
  )

  const docked = rect !== null
  const frameStyle: React.CSSProperties = fsMode === 'css'
    ? {
      // CSS-fallback: кадр сам занимает вьюпорт. z-index выше мини-угла и выше
      // нижней навигации — иначе «во весь экран» окажется под ней.
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100vw',
      height: '100vh',
      borderRadius: 0,
      zIndex: 10000,
    }
    : docked
    ? {
        // #642: absolute, не fixed — иначе на iOS кадр отстаёт от прокрутки.
        position: 'absolute',
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
        borderRadius: 20,
        zIndex: 40,
      }
    : {
        position: 'fixed',
        right: MINI_MARGIN,
        bottom: MINI_BOTTOM,
        width: MINI_WIDTH,
        height: MINI_HEIGHT,
        borderRadius: 12,
        zIndex: 1200,
        boxShadow: '0 8px 24px rgba(0,0,0,0.28)',
      }

  return (
    <MiniPlayerContext.Provider value={api}>
      {children}
      {source
        && createPortal(
          <div
            ref={frameRef}
            data-testid="mini-player"
            data-docked={docked ? 'true' : 'false'}
            data-fullscreen={fsMode !== 'off' ? 'true' : 'false'}
            style={{ ...frameStyle, overflow: 'hidden', background: '#000' }}
          >
            <iframe
              ref={iframeRef}
              key={source.key}
              src={source.url}
              title={source.title}
              style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
              allow="accelerometer; fullscreen; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
            {!docked && fsMode === 'off' && (
              <>
                {/* Кнопки только в мини-режиме: на самой лекции у плеера свои
                    контролы, и перекрывать их нельзя. */}
                <button
                  type="button"
                  aria-label="Вернуться к лекции"
                  onClick={() => navigate(source.returnTo)}
                  style={{
                    position: 'absolute', left: 4, top: 4, width: 26, height: 26,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    borderRadius: 8, border: 'none', cursor: 'pointer',
                    background: 'rgba(0,0,0,0.55)', color: '#fff', fontSize: 13, lineHeight: 1,
                  }}
                >
                  ⤢
                </button>
                <button
                  type="button"
                  aria-label="Закрыть мини-плеер"
                  onClick={close}
                  style={{
                    position: 'absolute', right: 4, top: 4, width: 26, height: 26,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    borderRadius: 8, border: 'none', cursor: 'pointer',
                    background: 'rgba(0,0,0,0.55)', color: '#fff', fontSize: 13, lineHeight: 1,
                  }}
                >
                  ✕
                </button>
              </>
            )}
          </div>,
          document.body,
        )}
    </MiniPlayerContext.Provider>
  )
}

/** null — провайдера нет (кураторская ветка дерева), плеер просто не работает. */
export function useMiniPlayer(): MiniPlayerApi | null {
  return useContext(MiniPlayerContext)
}

/**
 * Пришвартовать ролик к якорю на время жизни страницы. При уходе со страницы
 * кадр не выгружается — он уезжает в угол и продолжает играть.
 */
export function useDockedVideo(
  source: MiniPlayerSource | null,
  anchor: RefObject<HTMLElement>,
) {
  const player = useMiniPlayer()
  const key = source?.key ?? null

  useEffect(() => {
    if (!player || !source) return
    player.attach(source, anchor)
    return () => player.detach(source.key)
    // Пересоздаём только при смене ролика: attach/detach стабильны по useCallback.
  }, [player, key, anchor, source?.url, source?.returnTo, source?.title])
}
